import { useCallback, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import type { ResizableHeaderCellProps } from '@/components/ResizableHeaderCell';

export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 640;
const FALLBACK_WIDTH = 120;

type Widths = Record<string, number>;

export const clampWidth = (width: number) =>
  Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)));

const readStoredWidths = (storageKey: string): Widths => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, w]) => typeof w === 'number' && Number.isFinite(w))
    ) as Widths;
  } catch {
    return {};
  }
};

const storeWidths = (storageKey: string, widths: Widths) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    // Not remembered this time; the column keeps its width until reload.
  }
};

/**
 * Lets each column of an antd table be dragged narrower or wider, remembering
 * the widths per browser under `storageKey` so a layout survives a reload.
 * Pair it with ResizableHeaderCell as the table's header cell. Fixed columns
 * (Actions) keep their width, since the sticky edge depends on it.
 */
export const useResizableColumns = <T>(storageKey: string, columns: ColumnsType<T>) => {
  const [widths, setWidths] = useState<Widths>(() => readStoredWidths(storageKey));

  const setWidth = useCallback((key: string, width: number) => {
    setWidths((prev) => ({ ...prev, [key]: clampWidth(width) }));
  }, []);

  const resetWidth = useCallback((key: string) => {
    setWidths((prev) => {
      const rest = { ...prev };
      delete rest[key];
      storeWidths(storageKey, rest);
      return rest;
    });
  }, [storageKey]);

  /* Saved from the updater, not from the last render: a quick release lands
     before the final move has rendered, and saving then lost the width. */
  const persist = useCallback(() => {
    setWidths((current) => {
      storeWidths(storageKey, current);
      return current;
    });
  }, [storageKey]);

  const resizableColumns = useMemo(
    (): ColumnsType<T> =>
      columns.map((column) => {
        const key = String(column.key);
        if (column.fixed) return column;
        const defaultWidth = typeof column.width === 'number' ? column.width : FALLBACK_WIDTH;
        const width = widths[key] ?? defaultWidth;
        return {
          ...column,
          width,
          onHeaderCell: (): ResizableHeaderCellProps => ({
            resizeWidth: width,
            onResize: (next) => setWidth(key, next),
            onResizeEnd: persist,
            onResizeReset: () => resetWidth(key)
          })
        };
      }),
    [columns, widths, setWidth, resetWidth, persist]
  );

  /* The fixed Actions column only sticks when scroll.x matches the real
     total, so it follows the widths as they change. */
  const scrollX = useMemo(
    () =>
      resizableColumns.reduce(
        (sum, col) => sum + (typeof col.width === 'number' ? col.width : FALLBACK_WIDTH),
        0
      ),
    [resizableColumns]
  );

  return { columns: resizableColumns, scrollX };
};
